import React from "react";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { Button } from "@/components/ui/button/button";
import { IconFileSpreadsheet, IconX, IconRefresh } from "@tabler/icons-react";

type FileType = "image" | "csv";

interface FileUploadProps {
  label?: string;
  fileType?: FileType;
  value?: FileList | File;
  error?: string;
  onChange: (files: FileList | File | undefined) => void;
  previewUrl?: string | null;
  setPreviewUrl?: (url: string | null) => void;
  className?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
  label = "Upload File",
  fileType = "image",
  value,
  error,
  onChange,
  previewUrl,
  setPreviewUrl,
  className,
}) => {
  const [isDragging, setIsDragging] = React.useState(false);
  const [uploadedFile, setUploadedFile] = React.useState<FileList | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const isImage = fileType === "image";

  // Update uploadedFile when value changes
  React.useEffect(() => {
    if (value) {
      setUploadedFile(value as FileList);
    } else {
      setUploadedFile(null);
    }
  }, [value]);

  const handleFileButtonClick = () => fileInputRef.current?.click();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.target.files;
    if (files && files.length > 0) {
      setUploadedFile(files);
      onChange(files);

      if (isImage && setPreviewUrl) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrl(reader.result as string);
        };
        reader.readAsDataURL(files[0]);
      }
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      setUploadedFile(files);
      onChange(files);

      if (isImage && setPreviewUrl) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrl(reader.result as string);
        };
        reader.readAsDataURL(files[0]);
      }
    }
  };

  const handleDragEvents = (e: React.DragEvent<HTMLDivElement>, isEnter = true) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(isEnter);
  };

  // Format file size to be human readable
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' bytes';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    onChange(undefined);
    if (setPreviewUrl) setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="mb-4">
      {label && (
        <label className="text-sm text-muted font-medium block mb-1">
          {label}
        </label>
      )}

      <div
        onDrop={handleDrop}
        onDragOver={(e) => handleDragEvents(e)}
        onDragEnter={(e) => handleDragEvents(e, true)}
        onDragLeave={(e) => handleDragEvents(e, false)}
        className={cn(
          "space-y-2 border border-dashed bg-input-bgcolor p-6 rounded-md text-center",
          isDragging ? "border-blue-800/70" : "border-primaryborder",
          error && "border-red-500",
          className
        )}
      >
        {isImage && previewUrl ? (
          <div className="flex gap-4 items-center">
            <div className="w-34 h-34 mb-2 rounded-xl overflow-hidden border-2 border-primaryborder">
              <Image
                src={previewUrl}
                alt="Preview"
                width={96}
                height={96}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Button type="button" className="bg-[#1D1D26]" onClick={handleFileButtonClick}>
                Change Image
              </Button>
              <button
                type="button"
                className="text-white text-sm mb-2"
                onClick={handleRemoveFile}
              >
                Remove
              </button>
            </div>
          </div>
        ) : !isImage && uploadedFile ? (
          // CSV file preview
          <div className="flex flex-col items-center">
            <div className="bg-blue-200/20 p-4 mb-4 rounded-lg w-full max-w-md">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <IconFileSpreadsheet size={28} className="text-gray-400" />
                  <div className="text-left">
                    <p className="text-white font-medium truncate max-w-[200px]">
                      {uploadedFile[0].name}
                    </p>
                    <p className="text-gray-400 text-xs">
                      {formatFileSize(uploadedFile[0].size)}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleFileButtonClick}
                    className="p-1.5 hover:bg-gray-400/20 rounded-full"
                  >
                    <IconRefresh size={22} className="text-gray-400" />
                  </button>
                  <button
                    type="button"
                    onClick={handleRemoveFile}
                    className="p-1.5 hover:bg-gray-400/20 rounded-full"
                  >
                    <IconX size={22} className="text-red-400" />
                  </button>
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-400">
              CSV file uploaded successfully.
            </p>
          </div>
        ) : (
          // Default empty state
          <>
            <Image
              src={isImage ? "/assets/photo_icon.png" : "/assets/csv_icon.png"}
              alt="file icon"
              className="mx-auto mb-2 w-12 h-auto"
              width={48}
              height={48}
            />
            <p className="text-offwhite">
              {isImage ? "Choose an image file (logo or icon)." : "Upload Presale Whitelist (CSV)"}
            </p>
            <p className="text-xs text-muted mb-2">
              Drag & drop or click to browse
            </p>
            <Button type="button" className="bg-[#1D1D26]" onClick={handleFileButtonClick}>
              {isImage ? "Upload image" : "Upload CSV file"}
            </Button>
          </>
        )}

        <input
          type="file"
          accept={isImage ? "image/png,image/jpeg,image/webp,image/avif" : ".csv"}
          ref={fileInputRef}
          onChange={handleFileChange}
          className="hidden"
        />
      </div>

      {error && <p className="text-red-500 text-sm mt-1">{String(error)}</p>}
    </div>
  );
};

export default FileUpload;